import messaging from '@react-native-firebase/messaging';
import * as Notifications from 'expo-notifications';
import { PermissionStatus } from 'expo-permissions';
import { Platform } from 'react-native';
import * as RNPermissions from 'react-native-permissions';
import { BehaviorSubject } from 'rxjs';
import AsyncStorage, { Key } from '../../utils/asyncStorage';
import { makeDebug } from '../../utils/makeDebug';
import { openSettings } from '../../utils/openSettings';
import { isAndroidM } from '../../utils/platformUtils';
import { isDebugEnabled } from '../debugging';
import { useObservable } from '../hooks/useObservable';

const debug = makeDebug(
  'notification-permissions',
  isDebugEnabled('notification'),
);

export const notificationState$ = new BehaviorSubject<
  | { type: 'pending' }
  | { type: 'rejected' }
  | { type: 'granted'; token: string }
>({
  type: Platform.OS === 'web' ? 'rejected' : 'pending',
});

export const useCurrentNotificationState = () =>
  useObservable(notificationState$, 'current-user/notification-state');

export async function getPermissionsStatus(): Promise<PermissionStatus> {
  try {
    if (Platform.OS === 'web') {
      return PermissionStatus.GRANTED;
    }

    if (isAndroidM()) {
      const { status, settings } = await RNPermissions.checkNotifications();
      debug('android 13+ permission check result: ', status);
      if (status === 'granted') {
        debug('android 13+ permission settings: %O', settings);
      }
      if (status === 'granted') {
        const token = await messaging().getToken();
        notificationState$.next({ type: 'granted', token });
      }
      return status === 'granted'
        ? PermissionStatus.GRANTED
        : PermissionStatus.DENIED;
    } else {
      const settings = await Notifications.getPermissionsAsync();
      debug('get permissions result: ', settings);
      const granted =
        settings.granted ||
        settings.ios?.status ===
          Notifications.IosAuthorizationStatus.PROVISIONAL;
      if (granted) {
        const token = await messaging().getToken();
        notificationState$.next({ type: 'granted', token });
      }
      return settings.status;
    }
  } catch (e) {
    console.error(e);
    return PermissionStatus.UNDETERMINED;
  }
}

const checkAndRequestNotificationPermission = async () => {
  if (isAndroidM()) {
    debug('requesting notification permissions for android 13+... ');
    const { status, settings } = await RNPermissions.requestNotifications([
      'alert',
      'badge',
      'sound',
    ]);
    debug('android 13+ permission request result: ', status);
    if (status === 'granted') {
      debug('android 13+ permission settings: %O', settings);
    }
    void AsyncStorage.storeData(
      Key.AndroidNotificationPermissionStatus,
      status,
    );
    return status === 'granted';
  } else {
    const authStatus = await messaging().requestPermission();
    debug('default permission auth status: ', authStatus);
    return (
      authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
      authStatus === messaging.AuthorizationStatus.PROVISIONAL
    );
  }
};

export async function requestUserPermission() {
  if (Platform.OS === 'web') return;
  const enabled = await checkAndRequestNotificationPermission();
  debug('check and request permission result: ', enabled);
  if (!enabled) {
    notificationState$.next({ type: 'rejected' });
    return undefined;
  }
  const token = await messaging().getToken();
  debug('firebase messaging token: ', token);
  notificationState$.next({ type: 'granted', token });
}

export async function enableNotification(): Promise<void> {
  if (Platform.OS === 'web') return;

  await requestUserPermission();

  if (notificationState$.value.type === 'rejected') {
    await openSettings();
  }
}
